/**
 * DTF Town — My Coins page client.
 *
 * Renders the customer's coin wallet history on /pages/my-coins. All calls
 * go through the Shopify App Proxy:
 *   /apps/coin-rewards/<path>  ->  https://dash.dtftown.com/api/coins/<path>
 *
 * Source-of-truth rule: this script renders the exact numbers the dashboard
 * returns (balances, dollar_value, amount_coins, rules). It must NEVER
 * compute coin<->dollar conversions or hard-code program rules of its own —
 * showing a customer a wrong coin/dollar value would be worse than showing
 * nothing.
 */
(function () {
  'use strict';

  const API_BASE = '/apps/coin-rewards';
  const app = document.getElementById('coin-account-app');
  if (!app) return;

  const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };

  async function apiGet(path) {
    const res = await fetch(API_BASE + path, { headers, credentials: 'same-origin' });
    if (!res.ok) throw new Error('API error: ' + res.status);
    return res.json();
  }

  function esc(str) {
    return String(str ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  function coins(n) {
    return Number(n ?? 0).toLocaleString('en-US');
  }

  // Format a server-provided dollar amount. We only format what the API
  // gives us — we never derive dollars from coins on the client.
  function dollars(value) {
    return '$' + Number(value ?? 0).toFixed(2);
  }

  function fmtDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function fmtDateTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  // Human-friendly countdown for an active code's expiry.
  function expiresLabel(iso) {
    if (!iso) return '';
    const diff = new Date(iso).getTime() - Date.now();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / 3600000);
    if (hours >= 24) return 'Expires in ' + Math.floor(hours / 24) + 'd ' + (hours % 24) + 'h';
    if (hours >= 1) return 'Expires in ' + hours + 'h';
    return 'Expires in ' + Math.max(1, Math.floor(diff / 60000)) + 'm';
  }

  // ---- Balance summary -----------------------------------------------------

  async function loadBalance() {
    try {
      const b = await apiGet('/balance');
      setText('caAvailable', coins(b.total_available) + ' coins');
      setText('caAvailableDollars', '= ' + dollars(b.dollar_value) + ' in discounts');
      setText('caPending', coins(b.pending) + ' coins');
      setText('caEarned', coins(b.lifetime_earned) + ' coins');
      setText('caSpent', coins(b.lifetime_spent) + ' coins');
      setText('caTier', b.tier_label || b.tier || '—');
    } catch (e) {
      setText('caAvailable', 'Unavailable');
    }
  }

  function setText(id, text) {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  }

  // ---- Discount codes ------------------------------------------------------

  const STATE_META = {
    active: { label: 'Active', cls: 'is-active' },
    used: { label: 'Used', cls: 'is-used' },
    expired_unused: { label: 'Expired', cls: 'is-expired' },
    refunded: { label: 'Coins refunded', cls: 'is-refunded' },
  };

  function codeCard(code, showCopy) {
    const meta = STATE_META[code.state] || { label: code.state, cls: '' };
    const parts = [];
    parts.push('<div class="ca-code ' + meta.cls + '">');
    parts.push('<div class="ca-code__main">');
    parts.push('<span class="ca-code__value">' + esc(code.code) + '</span>');
    parts.push('<span class="ca-code__badge ' + meta.cls + '">' + esc(meta.label) + '</span>');
    parts.push('</div>');
    parts.push('<div class="ca-code__meta">');
    parts.push('<span>' + dollars(code.dollar_value) + ' off &middot; ' + coins(code.amount_coins) + ' coins</span>');

    if (code.state === 'active') {
      parts.push('<span class="ca-code__exp">' + esc(expiresLabel(code.expires_at)) + '</span>');
    } else if (code.state === 'used') {
      const on = code.order_name ? (' on order ' + esc(code.order_name)) : '';
      parts.push('<span>Used' + on + (code.applied_at ? ' &middot; ' + esc(fmtDate(code.applied_at)) : '') + '</span>');
    } else if (code.state === 'expired_unused') {
      parts.push('<span>Not used &middot; coins are being returned to your wallet</span>');
    } else if (code.state === 'refunded') {
      parts.push('<span>Unused &middot; coins returned to your wallet</span>');
    }
    parts.push('</div>');

    if (showCopy && code.state === 'active') {
      parts.push('<button type="button" class="ca-copy" data-code="' + esc(code.code) + '">Copy code</button>');
    }
    parts.push('</div>');
    return parts.join('');
  }

  async function loadCodes() {
    const activeEl = document.getElementById('caActiveCodes');
    const histEl = document.getElementById('caCodeHistory');
    try {
      const data = await apiGet('/redemptions');
      const all = data.codes || [];
      const active = all.filter((c) => c.state === 'active');
      const history = all.filter((c) => c.state !== 'active');

      if (active.length) {
        activeEl.innerHTML = active.map((c) => codeCard(c, true)).join('');
      } else {
        activeEl.innerHTML = '<p class="ca-empty">No active codes right now. Redeem coins on the rewards page to generate one.</p>';
      }

      if (history.length) {
        histEl.innerHTML = history.map((c) => codeCard(c, false)).join('');
      } else {
        histEl.innerHTML = '<p class="ca-empty">No past codes yet.</p>';
      }

      bindCopyButtons(activeEl);
    } catch (e) {
      activeEl.innerHTML = '<p class="ca-empty">Could not load your codes. Please refresh.</p>';
      histEl.innerHTML = '';
    }
  }

  function bindCopyButtons(scope) {
    scope.querySelectorAll('.ca-copy').forEach((btn) => {
      btn.addEventListener('click', () => {
        const code = btn.getAttribute('data-code');
        navigator.clipboard?.writeText(code).then(() => {
          const original = btn.textContent;
          btn.textContent = 'Copied!';
          setTimeout(() => { btn.textContent = original; }, 1800);
        }).catch(() => {});
      });
    });
  }

  // ---- Coin ledger ---------------------------------------------------------

  let ledgerPage = 0;
  let ledgerLastPage = 1;
  let ledgerLegacyDividerShown = false;

  function ledgerLegacyDivider() {
    return '<li class="ca-history-cutover">' +
      '<span class="ca-history-cutover__line" aria-hidden="true"></span>' +
      '<div class="ca-history-cutover__notice" role="note">' +
      '<strong>Previous coin system</strong>' +
      '<p>Coin activity shown below this line was recorded under our previous calculation system and may contain inaccuracies. ' +
      'We have now moved to a new calculation method. If you believe your coin balance is incorrect, please ' +
      '<a href="mailto:info@dtftown.com">contact us</a>.</p>' +
      '</div>' +
      '</li>';
  }

  function ledgerRow(tx) {
    const positive = Number(tx.amount) >= 0;
    const sign = positive ? '+' : '';
    const order = tx.order_name ? ' <span class="ca-row__order">' + esc(tx.order_name) + '</span>' : '';
    const pending = tx.status === 'pending' ? ' <span class="ca-row__pending">pending</span>' : '';
    // Active forward-only lots expose both their still-available amount and
    // expiry. Showing them together makes a partially used award unambiguous.
    const expiry = positive && tx.expires_at && tx.remaining_amount !== null
      ? ' <span class="ca-row__expiry">' + coins(tx.remaining_amount) + ' coins remaining &middot; ' + esc(expiresLabel(tx.expires_at)) + '</span>'
      : '';
    return '<li class="ca-row">' +
      '<div class="ca-row__info"><span class="ca-row__label">' + esc(tx.label || tx.category || 'Coin activity') + '</span>' + order + pending +
      '<span class="ca-row__date">' + esc(fmtDateTime(tx.created_at)) + '</span>' + expiry + '</div>' +
      '<span class="ca-row__amount ' + (positive ? 'is-pos' : 'is-neg') + '">' + sign + coins(tx.amount) + '</span>' +
      '</li>';
  }

  function ledgerRows(transactions) {
    return transactions.map((tx) => {
      let divider = '';
      if (tx.history_period === 'legacy' && !ledgerLegacyDividerShown) {
        ledgerLegacyDividerShown = true;
        divider = ledgerLegacyDivider();
      }

      return divider + ledgerRow(tx);
    }).join('');
  }

  async function loadLedger(append) {
    const listEl = document.getElementById('caLedger');
    const moreBtn = document.getElementById('caLedgerMore');
    const nextPage = ledgerPage + 1;
    if (!append) {
      ledgerLegacyDividerShown = false;
    }
    try {
      const data = await apiGet('/transactions?page=' + nextPage);
      ledgerPage = data.current_page || nextPage;
      ledgerLastPage = data.last_page || ledgerPage;
      const rows = ledgerRows(data.data || []);

      if (!append) {
        listEl.innerHTML = rows
          ? '<ul class="ca-rows">' + rows + '</ul>'
          : '<p class="ca-empty">No coin activity yet. Place an order to start earning.</p>';
      } else {
        const ul = listEl.querySelector('.ca-rows');
        if (ul) ul.insertAdjacentHTML('beforeend', rows);
      }

      moreBtn.hidden = ledgerPage >= ledgerLastPage;
    } catch (e) {
      if (!append) listEl.innerHTML = '<p class="ca-empty">Could not load your coin history. Please refresh.</p>';
      moreBtn.hidden = true;
    }
  }

  // ---- Rules ("how it works") ---------------------------------------------

  function renderRules(r) {
    const where = r.gang_sheet_only
      ? 'Coin discounts apply to gang sheet collections (DTF, UV, Sign and Banner printable goods). Blank apparel is excluded.'
      : 'Coin discounts apply across the store.';

    const facts = [
      ['Coin value', coins(r.coins_per_dollar) + ' coins = $1'],
      ['Minimum to redeem', coins(r.min_coins) + ' coins (' + dollars(r.min_coins / r.coins_per_dollar) + ')'],
      ['Most you can use per order', coins(r.max_coins_per_order) + ' coins (' + dollars(r.max_dollars_per_order) + ')'],
      ['Daily redemption limit', coins(r.daily_limit) + ' coins (' + dollars(r.daily_limit / r.coins_per_dollar) + ') per day'],
      ['Code validity', r.discount_code_ttl_hours + ' hours after you generate it'],
      ['SMS check', 'Redeeming ' + coins(r.sms_otp_threshold) + '+ coins at once needs a verified phone'],
      ['New coins expire', 'Newly earned coins must be used within ' + r.expiry_days + ' days'],
    ];

    const list = facts.map((f) =>
      '<div class="ca-rule"><span class="ca-rule__k">' + esc(f[0]) + '</span><span class="ca-rule__v">' + esc(f[1]) + '</span></div>'
    ).join('');

    return list +
      '<p class="ca-rules__note">' + esc(where) + '</p>' +
      '<details class="ca-faq"><summary>Why does my code say "doesn\'t apply"?</summary>' +
      '<p>Usually one of: the cart has no eligible printable items, the code is more than ' + r.discount_code_ttl_hours + ' hours old, or you\'re signed into a different account than the one that generated it. Codes are locked to your account and can\'t be shared.</p></details>' +
      '<details class="ca-faq"><summary>What happens if I don\'t use a code?</summary>' +
      '<p>Unused codes expire after ' + r.discount_code_ttl_hours + ' hours and the coins are automatically returned to your wallet.</p></details>';
  }

  async function loadRules() {
    const el = document.getElementById('caRules');
    try {
      const r = await apiGet('/rules');
      el.innerHTML = renderRules(r);
      // Keep the coin-history expiry warning in lockstep with config.
      const note = document.getElementById('caLedgerExpiryNote');
      if (note && r.expiry_days) {
        note.innerHTML = '<strong>Use new coins within ' + Number(r.expiry_days) + ' days.</strong> ' +
          'Newly earned coins expire after ' + Number(r.expiry_days) +
          ' days; check the countdown in your coin history.';
      }
    } catch (e) {
      el.innerHTML = '<p class="ca-empty">Could not load the rules right now.</p>';
    }
  }

  // ---- Init ----------------------------------------------------------------

  document.getElementById('caLedgerMore')?.addEventListener('click', () => loadLedger(true));

  loadBalance();
  loadCodes();
  loadLedger(false);
  loadRules();
})();
